import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import Select from 'react-select';
import { FixedLists } from 'core';
import { UserContext } from 'contexts/user';
import DiscountPercentBadge from '../../../modules/productForm/components/Blocks/DiscountPercentBadge';
import { useMediaQuery, usePreventBodyScroll } from '../../../libs';
import { HistoryContext } from '../../../contexts/history';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '../../../queries/queryKeys';
import { useGa4Context } from '../../../contexts/ga4';
import { getAccountType, getVisibilityUpgradeType } from '../../../libs/ga4/helpers';

const PromotionCheckoutModal = (props) => {
  const { data, callback, langs, getLang, CONFIG, currency } = props;
  const { params } = useContext(HistoryContext);
  const ga4Analytics = useGa4Context();

  const queryClient = useQueryClient();

  const flashSaleData = queryClient.getQueryData([QueryKeys.FlashSalesData])?.data;
  const PromPricesflashSaleData = data?.formattedPromPrices?.FLASH_SALE;

  const chosenProductHasFlashSale =
    flashSaleData && PromPricesflashSaleData && data.pollAction?.products?.length === 1 && Number(data.pollAction?.products[0]) === flashSaleData.product_id;

  const isMobile = useMediaQuery('(max-width: 768px)');
  usePreventBodyScroll();

  const promList = FixedLists.promsList(langs);
  const { accountDetails, shop } = useContext(UserContext);
  const hasTrackedView = useRef(false);
  const [autoHour, setAutoHour] = useState({ value: 0, label: `00:00` });
  const [activeList, setActiveList] = useState([]);

  const [updateData, setUpdateData] = useState({ vip: null, update: null, color: null });

  const [activePromo, setActivePromo] = useState(0);

  useEffect(() => {
    if (hasTrackedView.current || !data.source || !data.pollAction?.productsList?.length) return;
    hasTrackedView.current = true;
    data.pollAction.productsList.forEach((product) => ga4Analytics.visibilityUpgradeView({
      listingId: product.id,
      source: data.source,
      sellerType: getAccountType(shop),
      categoryId: product.catId,
    }));
  }, [data, ga4Analytics, shop]);

  const totalPromotionsPrice = useMemo(() => {
    return Object.values(updateData).reduce((sum, item) => {
      if (item && item.totalPrice) {
        return sum + item.totalPrice;
      }
      return sum;
    }, 0);
  }, [updateData]);

  const totalOriginalPrice = useMemo(() => {
    return Object.values(updateData).reduce((sum, item) => {
      if (item && item.totalOriginalPrice) {
        return sum + item.totalOriginalPrice;
      }
      return sum;
    }, 0);
  }, [updateData]);

  const showTotalOriginalPrice = Number(totalOriginalPrice) > Number(totalPromotionsPrice);

  const getInitialPrice = (promName) => {
    if (!data.promPrices) return null;

    return data?.promPrices[promName]?.List[0]?.options[0]?.price || 0;
  };

  const getOriginalPrice = (promName) => {
    if (!data.promPrices) return null;

    return data?.promPrices[promName]?.OriginalPrice || 0;
  };

  const defValues = {
    20: { value: 1, label: `1 ${langs.Day}`, price: getInitialPrice('SUPER_VIP'), original_price: getOriginalPrice('SUPER_VIP') },
    15: { value: 1, label: `1 ${langs.Day}`, price: getInitialPrice('VIP_PLUS'), original_price: getOriginalPrice('VIP_PLUS') },
    10: { value: 1, label: `1 ${langs.Day}`, price: getInitialPrice('VIP'), original_price: getOriginalPrice('VIP') },
    1: { value: 1, label: `1 ${langs.Day}`, price: getInitialPrice('COLOR'), original_price: getOriginalPrice('COLOR') },
    2: { value: 1, label: `1 ${langs.Day}`, price: getInitialPrice('AUTO_UPDATE'), original_price: getOriginalPrice('AUTO_UPDATE') },
    3: { value: 1, label: `1 ${langs.Day}`, price: getInitialPrice('UPDATE'), original_price: getOriginalPrice('UPDATE') },
  };

  // Closing the modal is a state update, so the button stays live for a frame after the first
  // press. Without this a double-click runs onPromotionSelect twice and creates two drafts.
  const submitted = useRef(false);

  const success = () => {
    if (submitted.current) return;
    submitted.current = true;

    const promotions = [];

    Object.values(updateData).forEach((e) => {
      if (e) {
        const newObj = { ...e };

        newObj['UseFlashSaleDiscount'] = null;
        newObj['FlashSaleDiscountType'] = null;

        if (newObj.promHandler.flashSale_enabled) {
          newObj['UseFlashSaleDiscount'] = 1;
          newObj['FlashSaleDiscountType'] = newObj.promHandler.flashSale_discount_type;
        }

        promotions.push(newObj);
      }
    });

    promotions.forEach((promotion) => {
      const upgradeType = getVisibilityUpgradeType(promotion.activeOption);
      if (!upgradeType) return;
      data.pollAction.productsList.forEach((product) => ga4Analytics.visibilityUpgradeSelect({
        listingId: product.id,
        upgradeType,
        dailyPrice: promotion.promHandler?.price,
        durationDays: promotion.Quantity,
        source: data.source,
        sellerType: getAccountType(shop),
      }));
    });

    callback(
      {
        products: data.pollAction.products,
        hour: autoHour.value,
        price: totalPromotionsPrice,
        promotions,
      },
      'success',
    );
    callback(null, 'destroy');
  };

  const processList = (ids, vip = false) => {
    let newList = [...promList];
    newList = newList.filter((o) => ids.includes(String(o.id)));
    if (vip) {
      newList = newList.filter((o) => o.id >= vip);
    }
    setActiveList(newList);
  };

  // const handleSelects = (promID, obj = false) => {
  //   let newData = { ...defValues };
  //   let total = 0;
  //   Object.entries(newData).map(([key, value]) => {
  //     if (parseInt(key) === promID) {
  //       newData[key] = obj;
  //       let prs = data.pollAction.products;
  //       total = parseInt(newData[key].value) * parseFloat(newData[key].price) * (prs && prs.length ? prs.length : 1);
  //     } else {
  //       newData[key] = defValues[key];
  //     }
  //     return key;
  //   });
  //
  //   setPriceToPay(total);
  //   setPromHandlers(newData);
  //   setActivePromotion(promList.find((o) => o.id === promID));
  // };

  const handleSelectsNew = (prom, daysData, disableOption, selectedHour = autoHour.value) => {
    let prs = data.pollAction.products;

    switch (prom.typeID) {
      case 4:
        let vipDayPrice = daysData ? daysData.price : defValues[prom.id].price;
        let vipOriginalPrice = daysData ? daysData.original_price : Number(defValues[prom.id].original_price);

        let vipTotalPrice = vipDayPrice * (prs && prs.length ? prs.length : 1);
        let vipTotalOriginalPrice = vipOriginalPrice * (prs && prs.length ? prs.length : 1);

        if (vipOriginalPrice === 0) {
          vipTotalOriginalPrice = vipTotalPrice;
        }

        if (daysData) {
          vipTotalPrice = vipTotalPrice * daysData.value;
          vipTotalOriginalPrice = vipTotalOriginalPrice * daysData.value;
        }

        let vipObj = {
          TypeID: 4,
          OrderValue: prom.id,
          Quantity: daysData?.value ?? 1,
          activeOption: prom.id,
          totalPrice: vipTotalPrice,
          totalOriginalPrice: vipTotalOriginalPrice,
          promHandler: daysData ?? defValues[prom.id],
        };

        if (updateData.vip && updateData.vip.OrderValue === vipObj.OrderValue && disableOption) {
          setUpdateData((data) => ({ ...data, vip: null }));
        } else {
          setUpdateData((data) => ({ ...data, vip: vipObj }));
        }
        break;
      case 5:
        let colorDayPrice = daysData ? daysData.price : defValues[prom.id].price;
        let colorDayOriginalPrice = daysData ? daysData.original_price : Number(defValues[prom.id].original_price);

        let colorTotalPrice = colorDayPrice * (prs && prs.length ? prs.length : 1);
        let colorTotalOriginalPrice = colorDayOriginalPrice * (prs && prs.length ? prs.length : 1);

        if (colorDayPrice === 0) {
          colorTotalOriginalPrice = colorTotalPrice;
        }

        if (daysData) {
          colorTotalPrice = colorTotalPrice * daysData.value;
          colorTotalOriginalPrice = colorTotalOriginalPrice * daysData.value;
        }

        const colorObj = {
          TypeID: 5,
          Quantity: daysData?.value ?? 1,
          activeOption: prom.id,
          totalPrice: colorTotalPrice,
          totalOriginalPrice: colorTotalOriginalPrice,
          promHandler: daysData ?? defValues[prom.id],
        };

        if (updateData.color && disableOption) {
          setUpdateData((data) => ({ ...data, color: null }));
        } else {
          setUpdateData((data) => ({ ...data, color: colorObj }));
        }
        break;
      case 2:
        let updateDayPrice = daysData ? daysData.price : defValues[prom.id].price;
        let updateOriginalPrice = daysData ? daysData.original_price : Number(defValues[prom.id].original_price);

        let updateTotalPrice = updateDayPrice * (prs && prs.length ? prs.length : 1);
        let updateTotalOriginalPrice = updateOriginalPrice * (prs && prs.length ? prs.length : 1);

        if (updateDayPrice === 0) {
          updateTotalOriginalPrice = updateTotalPrice;
        }

        if (daysData) {
          updateTotalPrice = updateTotalPrice * daysData.value;
          updateTotalOriginalPrice = updateTotalOriginalPrice * daysData.value;
        }

        let updateObj = {
          TypeID: 2,
          UpdateTypeID: prom.updateTypeID ?? null,
          Quantity: daysData?.value ?? 1,
          activeOption: prom.id,
          totalPrice: updateTotalPrice,
          totalOriginalPrice: updateTotalOriginalPrice,
          promHandler: daysData ?? defValues[prom.id],
        };

        if (prom.updateTypeID === 1) {
          updateObj.Hours = selectedHour ?? 0;
        }

        if (updateData.update && updateData.update.UpdateTypeID === updateObj.UpdateTypeID && disableOption) {
          setUpdateData((data) => ({ ...data, update: null }));
        } else {
          setUpdateData((data) => ({ ...data, update: updateObj }));
        }
        break;
      default:
        break;
    }
  };

  const draftPromId = (promotion) => {
    if (Number(promotion.typeId) === 4) return Number(promotion.orderValue);
    if (Number(promotion.typeId) === 5) return 1;
    return Number(promotion.updateTypeId) === 1 ? 2 : 3;
  };

  useEffect(() => {
    const draftPromotions = data.pollAction?.draftPromotions;
    if (!draftPromotions?.length || !data.formattedPromPrices) return;

    draftPromotions.forEach((promotion) => {
      const prom = promList.find((o) => o.id === draftPromId(promotion));
      if (!prom) return;

      const options = (data.formattedPromPrices[prom.name]?.List ?? []).flatMap((segment) => segment.options ?? []);
      const daysData = options.find((option) => Number(option.value) === Number(promotion.quantity));
      const selectedHour = Number(promotion.hours?.[0] ?? 0);

      if (prom.id === 2) {
        setAutoHour({ value: selectedHour, label: `${selectedHour < 10 ? `0${selectedHour}` : selectedHour}:00` });
      }

      handleSelectsNew(prom, daysData, false, selectedHour);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.pollAction?.draftPromotions, data.formattedPromPrices]);

  useEffect(() => {
    if (params.promID && PromPricesflashSaleData && flashSaleData) {
      const prom = promList.find((o) => o.id === Number(params.promID));
      const flashSaleDaysQuantity = PromPricesflashSaleData.quantity;
      handleSelectsNew(
        prom,
        {
          value: String(flashSaleDaysQuantity),
          price: PromPricesflashSaleData.price,
          original_price: PromPricesflashSaleData.original_price,
          label: `${flashSaleDaysQuantity} დღე`,
          flashSale_enabled: true,
          flashSale_discount_type: PromPricesflashSaleData.discount_offer_type,
        },
        false,
      );
    }
  }, [params, PromPricesflashSaleData]);

  const getCurrentPrice = (promID, promTypeName) => {
    const isCurrentPromIdSelected = updateData[promTypeName]?.activeOption === promID;

    const currentPrice = parseFloat(isCurrentPromIdSelected ? updateData[promTypeName]?.promHandler.price : defValues[promID]?.price);
    return currentPrice > 0 ? currentPrice : 0;
  };

  const getOriginalPriceFromPromHandlers = (promID, promTypeName) => {
    const isCurrentPromIdSelected = updateData[promTypeName]?.activeOption === promID;

    const currentPrice = parseFloat(isCurrentPromIdSelected ? updateData[promTypeName]?.promHandler?.original_price : defValues[promID]?.original_price);
    return currentPrice > 0 ? currentPrice : 0;
  };

  const openVips = () => {
    processList(CONFIG.PROM_VIPS_LIST, data.pollAction.activeVip);
    setActivePromo(1);
  };

  const openColor = () => {
    processList(CONFIG.PROM_COLORS_LIST);
    setActivePromo(2);
  };

  const openUpdates = () => {
    processList(CONFIG.PROM_UPDATES_LIST);
    setActivePromo(3);
  };

  useEffect(() => {
    accountDetails();
    let type = data.pollAction.type;
    let activeVip;
    if (data.pollAction.productsList.length > 0) {
      let filterVip = data.pollAction.productsList.filter((o) => o.vip > 0);
      activeVip = Math.max.apply(
        Math,
        filterVip.map((o) => o.vip),
      );
    } else {
      activeVip = data.pollAction.activeVip;
    }
    if (type === 1) {
      processList(CONFIG.PROM_VIPS_LIST, activeVip);
      setActivePromo(1);
    } else if (type === 2) {
      processList(CONFIG.PROM_COLORS_LIST);
      setActivePromo(2);
    } else if (type === 3) {
      processList(CONFIG.PROM_UPDATES_LIST);
      setActivePromo(3);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const promTabMap = [
    {
      name: langs.NM_vip_buy,
      promoID: 1,
      onClick: openVips,
      logoClassname: 'svg-i rocket-proms',
      title: langs.ChooseVipService,
      description: langs.VipDescription,
      updateDataKey: 'vip',
    },
    {
      name: langs.AddColor,
      promoID: 2,
      onClick: openColor,
      logoClassname: 'svg-i color-proms',
      title: langs.AddColor,
      description: langs.ColorDescription,
      updateDataKey: 'color',
    },
    {
      name: langs.APP_update,
      promoID: 3,
      onClick: openUpdates,
      logoClassname: 'svg-i refresh-proms',
      title: langs.APP_update,
      description: langs.UpdateDescription,
      updateDataKey: 'update',
    },
  ];

  const activePromotTab = promTabMap.find((item) => item.promoID === activePromo);
  const isOptionSelectedFromActiveTab = !!updateData[activePromotTab?.updateDataKey];
  const onClearClick = (promKey) => {
    setUpdateData((data) => ({ ...data, [promKey]: null }));
  };

  return (
    <div className="promos-modal-container promotion-checkout-modal w-100 d-flex flex-column flex-md-row promotions-scroll">
      <div className={'hidden-on-desktop'} style={{ padding: '16px 20px', paddingLeft: '20px', borderBottom: '1px solid #E7EAF3' }}>
        <span className={'font-medium'} style={{ fontSize: '14px' }}>
          {langs?.NM_PersonalBlocks[8]}
        </span>
        <div className="font-base" style={{ color: '#6F7383', fontSize: '10px', marginTop: '4px' }}>
          {langs.IsSelectedCount} {props.data?.pollAction?.products?.length} {langs.APP_state}
        </div>
      </div>
      <div
        className={`${!isMobile && 'acceleration-promotions'}`}
        style={{
          padding: '24px',
          paddingTop: isMobile ? '20px' : '60px',
          paddingBottom: isMobile ? '20px' : '24px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'space-between',
          borderRight: isMobile ? 'none' : '1px solid #E7EAF3',
          borderBottom: isMobile ? '1px solid #E7EAF3' : 'none',
        }}
      >
        {/*<div className="pb-24px text-dark font-bold">{langs.NM_PersonalBlocks[8]}</div>*/}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} className={'responsive-promo-tabs'}>
          {promTabMap.map((item, index) => {
            const isTabBeingViewed = activePromo === item.promoID;
            const isOptionFromTabSelected = !!updateData[item.updateDataKey];

            return (
              <div
                className={`cursor-pointer ${isOptionFromTabSelected ? 'promo-tab-title-option-selected' : 'promo-tab-title'} ${
                  isTabBeingViewed ? 'active' : ''
                }`}
                onClick={item.onClick}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: isMobile ? '8px 8px' : '16px 12px',
                  paddingRight: isMobile ? '16px' : '12px',
                  borderRadius: '8px',
                  position: 'relative',
                }}
                key={index}
              >
                <span className={`${item.logoClassname}`} style={{ marginRight: '8px' }} />
                {isOptionFromTabSelected && (
                  <span
                    className={'svg-i green-checkmark-icon'}
                    style={{ position: 'absolute', right: isMobile ? '6px' : '12px', top: '50%', transform: 'translateY(-50%)' }}
                  />
                )}
                <span className={`${isTabBeingViewed ? 'font-medium' : 'font-base'}`} style={{ maxWidth: isMobile ? '40px' : '100%' }}>
                  {item.name}
                </span>
              </div>
            );
          })}
        </div>
        <div className="font-size-12 font-medium hidden-on-mobile" style={{ color: '#161616' }}>
          {langs.IsSelectedCount} {props.data?.pollAction?.products?.length} {langs.APP_state}
        </div>
      </div>
      <div className="flex-1" style={{ padding: isMobile ? '16px 20px' : 0, background: isMobile ? '#F8F9FD' : 'transparent' }}>
        <div
          className="position-relative h-100"
          style={{
            background: isMobile ? 'white' : 'transparent',
            padding: isMobile ? '16px' : 0,
            borderRadius: isMobile ? '8px' : 0,
            border: isMobile ? '1px solid #E7EAF3' : 'none',
          }}
        >
          <div className="h-100">
            <div
              className="scrolled-promotion w-100 d-flex flex-column justify-content-between h-100"
              style={{ paddingTop: isMobile ? 0 : '60px', paddingBottom: '24px' }}
            >
              <div className="font-size-12" style={{ padding: isMobile ? '0' : '0 24px' }}>
                <div
                  style={{
                    fontSize: isMobile ? '12px' : '16px',
                    borderBottom: isMobile ? '1px solid #E7EAF3' : 'none',
                    paddingBottom: isMobile ? '16px' : '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className={`${isMobile ? 'font-medium' : 'font-bold'}`}>
                    <span className={`${activePromotTab?.logoClassname} hidden-on-desktop`} style={{ width: '16px', height: '16px' }} />
                    {activePromotTab?.title}
                  </div>
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
                    className={'hidden-on-desktop'}
                    onClick={() => onClearClick(activePromotTab.updateDataKey)}
                  >
                    <span className={'svg-i delete-bin-promotions'} style={{ color: 'red', opacity: isOptionSelectedFromActiveTab ? '1' : '0.5' }} />
                    <span className={'font-base hidden-on-desktop'} style={{ fontSize: '10px', color: isOptionSelectedFromActiveTab ? '#3D3F4B' : '#A4AEC1' }}>
                      {langs.Clear}
                    </span>
                  </div>
                </div>
                <div className={`${isMobile ? 'font-base' : 'font-medium'}`} style={{ fontSize: isMobile ? '10px' : '12px', marginTop: '16px' }}>
                  {activePromotTab?.description}
                </div>
              </div>
              <div style={{ paddingBottom: isMobile ? '90px' : '0' }}>
                {activeList.length > 0 &&
                  activeList.map((prom, i) => {
                    let price = getCurrentPrice(prom.id, prom.typeName);
                    let originalPrice = getOriginalPriceFromPromHandlers(prom.id, prom.typeName);

                    const isOptionSelected = updateData[prom.typeName] && updateData[prom.typeName].activeOption === prom.id;

                    let list = data.formattedPromPrices[prom.name]?.List ?? [];

                    const showDiscountBadge = list.some((segment) => segment.original_price && Number(segment.price) !== Number(segment.original_price));

                    if (prom.id === Number(PromPricesflashSaleData?.discount_offer_type) && chosenProductHasFlashSale) {
                      list = list.map((segment) => {
                        const hasFlashSaleOption = segment.options.some((item) => Number(item.value) === Number(PromPricesflashSaleData.quantity));

                        if (hasFlashSaleOption) {
                          return {
                            ...segment,
                            options: segment.options.map((option) => {
                              if (Number(option.value) === Number(PromPricesflashSaleData.quantity)) {
                                return {
                                  ...option,
                                  price: Number(PromPricesflashSaleData.price),
                                  original_price: Number(PromPricesflashSaleData.original_price),
                                  flashSale_enabled: true,
                                  flashSale_discount_type: PromPricesflashSaleData.discount_offer_type,
                                };
                              }
                              return option;
                            }),
                          };
                        }
                        return segment;
                      });
                    }

                    return (
                      <div
                        className={`d-flex promo-item flex-md-row w-100 ${isMobile ? '' : 'px-md-32px px-20px'} ${
                          prom.disabled ? 'opacity-50 pointer-events-none' : ''
                        }`}
                        data-prom-id={prom.id}
                        key={i}
                        onClick={() => {
                          handleSelectsNew(prom, undefined, true);
                        }}
                        style={{
                          padding: isMobile ? '0' : '16px 0',
                          cursor: 'pointer',
                          transition: 'background-color 0.1s ease',
                          alignItems: 'center',
                          flexDirection: 'column',
                        }}
                      >
                        <div
                          className={`d-flex align-items-center w-100 width-md-initial flex-grow-1 cursor-pointer promo-item-hover ${
                            prom.id === 3 ? 'flex-shrink ' : ''
                          }`}
                          style={{
                            borderRadius: '8px',
                          }}
                        >
                          <div className="pr-20px cursor-pointer">
                            <div
                              className="w-basis-20px h-20px rounded-circle d-flex align-items-center justify-content-center"
                              style={{
                                border: isOptionSelected ? 'none' : '1px solid #DADBDD',
                                backgroundColor: isOptionSelected ? '#26b753' : 'transparent',
                              }}
                            >
                              {isOptionSelected && (
                                <div
                                  className="rounded-circle"
                                  style={{
                                    width: '8px',
                                    height: '8px',
                                    backgroundColor: 'white',
                                  }}
                                ></div>
                              )}
                            </div>
                          </div>
                          <span className={`${prom.mainClassName} d-flex align-items-center justify-content-center w-basis-40px h-40px border-radius-md`}>
                            <i className={`svg-i svg-i-big ${prom.iconClassName}`} style={{ position: 'relative', fontStyle: 'normal' }}>
                              {/*{showDiscountBadge && (*/}
                              {/*  <DiscountPercentBadge*/}
                              {/*    style={{*/}
                              {/*      position: 'absolute',*/}
                              {/*      top: -15,*/}
                              {/*      left: -15,*/}
                              {/*      width: '16px',*/}
                              {/*      height: '16px',*/}
                              {/*      fontStyle: '!normal',*/}
                              {/*    }}*/}
                              {/*    tooltiptext={langs[`DiscountBadgeTooltipText_id_${prom.id}`]}*/}
                              {/*  />*/}
                              {/*)}*/}
                            </i>
                          </span>
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'column',
                              maxHeight: 'fit',
                              flexBasis: '180px',
                              justifyContent: isMobile ? 'space-between' : 'center',
                              marginLeft: '12px',
                              fontSize: isMobile ? '12px' : '14px',
                              gap: '2px',
                              width: '100%',
                            }}
                          >
                            {/*<span className="font-medium">{prom.title}</span>*/}
                            {/*<span className={'font-base hidden-on-mobile'} style={{ fontSize: '12px', color: '#6F7383' }}>*/}
                            {/*  1 {langs.Day} - {price} ₾*/}
                            {/*</span>*/}

                            {originalPrice && Number(originalPrice) > 0 && Number(originalPrice) !== Number(price) ? (
                              <div>
                                <div className="font-medium">{prom.title}</div>
                                <div>
                                  <span className="font-bold font-size-14 text-nowrap mr-6px" style={{ color: '#FF3B30' }}>
                                    {price} ₾
                                  </span>
                                  <span className="font-medium font-size-14 text-nowrap" style={{ color: '#7A8290', textDecoration: 'line-through' }}>
                                    {originalPrice} ₾
                                  </span>
                                  <span style={{ fontSize: '12px', color: '#6F7383' }}>/ 1 დღე</span>
                                </div>
                              </div>
                            ) : (
                              <>
                                <span className="font-medium">{prom.title}</span>
                                <span className={'font-base hidden-on-mobile'} style={{ fontSize: '12px', color: '#6F7383' }}>
                                  1 {langs.Day} - {price} ₾
                                </span>
                              </>
                            )}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '1px', flex: 1, alignItems: 'end' }} className={'hidden-on-desktop'}>
                            <span className={'font-base'} style={{ fontSize: '10px', color: '#8C929B' }}>
                              1 {langs.Day}
                            </span>
                            <span className={'font-medium'} style={{ fontSize: '14px' }}>
                              {price}₾
                            </span>
                          </div>
                          {/*<div className="d-flex flex-column ml-auto" style={{ justifyContent: 'center', alignItems: 'start', minWidth: '80px' }}>*/}
                          {/*  <span className="font-size-13 text-nowrap text-gray-light">{prom.period}</span>*/}
                          {/*  {originalPrice && originalPrice > 0 ? (*/}
                          {/*    <div>*/}
                          {/*      <span className="font-bold font-size-14 text-nowrap mr-6px" style={{ color: '#FF3B30' }}>*/}
                          {/*        {price} ₾*/}
                          {/*      </span>*/}
                          {/*      <span className="font-medium font-size-14 text-nowrap" style={{ color: '#7A8290', textDecoration: 'line-through' }}>*/}
                          {/*        {originalPrice} ₾*/}
                          {/*      </span>*/}
                          {/*    </div>*/}
                          {/*  ) : (*/}
                          {/*    <span className="font-medium font-size-14 text-nowrap" style={{ color: '#222' }}>*/}
                          {/*      {price} ₾*/}
                          {/*    </span>*/}
                          {/*  )}*/}
                          {/*</div>*/}
                        </div>
                        <div className={`d-flex align-items-center w-100 width-md-initial ml-md-auto`} style={{ marginTop: isMobile ? '12px' : '0' }}>
                          <div className="ml-auto flex-grow-1 flex-md-grow-0 d-flex">
                            {prom.id === 3 ? (
                              <></>
                            ) : (
                              <div
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginBottom: isMobile ? '13px' : 0 }}
                                className={'sg-selectbox-container sg-menu-right sg-wide-menu promo-selectbox-menu group-options-menu w-100 ml-md-28px'}
                              >
                                <Select
                                  name="Days"
                                  classNamePrefix="sg-selectbox"
                                  placeholder=""
                                  isSearchable={false}
                                  options={list}
                                  formatOptionLabel={(option) => {
                                    return (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        {option.value} დღე{' '}
                                        {option.flashSale_enabled ? <i className={'svg-i flash-sale-discount'} style={{ marginTop: '4px' }} /> : null}
                                      </div>
                                    );
                                  }}
                                  formatGroupLabel={(option) =>
                                    option.original_price && Number(option.original_price) !== Number(option.price) ? (
                                      <div style={{ fontSize: '12px', paddingRight: 0 }}>
                                        <span className={'font-bold'} style={{ color: '#222222' }}>
                                          {option.label}. {props.langs.Day}:{' '}
                                        </span>
                                        <span className={'font-bold'} style={{ color: '#FF3B30' }}>
                                          {option.price} ₾
                                        </span>
                                        <span className={'font-medium'} style={{ textDecoration: 'line-through', color: '#7A8290', marginLeft: '4px' }}>
                                          {option.original_price} ₾
                                        </span>
                                      </div>
                                    ) : (
                                      <div style={{ fontSize: '12px', paddingRight: 0 }}>
                                        <span className={'font-bold'} style={{ color: '#222222' }}>
                                          {option.label}. {props.langs.Day}: {option.price} ₾
                                        </span>
                                      </div>
                                    )
                                  }
                                  onChange={(value) => {
                                    handleSelectsNew(prom, value);
                                  }}
                                  value={updateData[prom.typeName]?.activeOption === prom.id ? updateData[prom.typeName]?.promHandler : defValues[prom.id]}
                                  isDisabled={prom.disabled}
                                />
                              </div>
                            )}
                          </div>
                          {prom.id === 2 && (
                            <div
                              className={`ml-8px w-basis-md-80px flex-grow-1 flex-md-grow-0 proms-select`}
                              style={{ marginBottom: isMobile ? '13px' : 0 }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Select
                                name="PromBlockAutoUpdateHour"
                                className="sg-selectbox-container group-options-menu w-100"
                                classNamePrefix="sg-selectbox"
                                placeholder=""
                                isSearchable={false}
                                noOptionsMessage={() => langs.NM_NothingFound}
                                options={[...Array(24)].map((item, i) => (item = { value: i, label: `${i < 10 ? `0${i}` : i}:00` }))}
                                onChange={(value) => {
                                  setAutoHour(value);

                                  if (updateData.update && updateData.update.UpdateTypeID === 1) {
                                    setUpdateData({ ...updateData, update: { ...updateData.update, Hours: value.value } });
                                  }
                                }}
                                value={autoHour}
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
              </div>

              <div
                style={{ borderTop: '1px solid #E9EAEB', paddingTop: '24px' }}
                className="d-flex align-items-center justify-content-between px-24px mt-auto hidden-on-mobile"
              >
                {!!totalPromotionsPrice ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'start' }}>
                    <span className={'font-base'} style={{ fontSize: '12px', color: '#52555F' }}>
                      {langs?.SumPrice}
                    </span>
                    <div className="text-nowrap" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span className={'font-medium'} style={{ fontSize: '20px', color: '#272A37' }}>
                        {totalPromotionsPrice.toFixed(2)} {currency()}
                      </span>
                      {showTotalOriginalPrice && (
                        <span className={'font-base'} style={{ fontSize: '16px', color: '#6F7383', textDecoration: 'line-through' }}>
                          {totalOriginalPrice.toFixed(2)} {currency()}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ minHeight: '56px' }}></div>
                )}
                <button
                  className="btn d-inline-flex align-items-center h-52px border-radius font-size-14 py-15px bg-primary font-medium"
                  onClick={success}
                  style={{ color: '#222', transition: 'all 0.3s ease' }}
                  disabled={!totalPromotionsPrice || totalPromotionsPrice === 0}
                >
                  {langs.Continue}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div
        className={'hidden-on-desktop'}
        style={{
          position: 'fixed',
          height: '80px',
          background: 'white',
          bottom: 0,
          borderTop: '1px solid #E7EAF3',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '16px 20px',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'start' }}>
          <span className={'font-base'} style={{ color: '#9CA1AA', fontSize: '12px' }}>
            {langs.SumPrice}
          </span>
          <span className="text-nowrap font-medium" style={{ fontSize: '20px' }}>
            {totalPromotionsPrice ? (
              <span>
                {totalPromotionsPrice.toFixed(2)} {currency()}{' '}
                {showTotalOriginalPrice && (
                  <span className={'font-base'} style={{ fontSize: '16px', color: '#6F7383', textDecoration: 'line-through' }}>
                    {totalOriginalPrice.toFixed(2)} {currency()}
                  </span>
                )}
              </span>
            ) : (
              '0.00'
            )}
          </span>
        </div>
        <div className="d-flex align-items-center justify-content-end">
          <button
            className="btn d-inline-flex align-items-center h-48px border-radius font-size-14 py-15px bg-primary font-medium"
            onClick={success}
            style={{ color: '#222', transition: 'all 0.3s ease' }}
            disabled={!totalPromotionsPrice || totalPromotionsPrice === 0}
          >
            {langs.Continue}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromotionCheckoutModal;
